import {useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import {useRouter} from '@s-ui/react-router'
import {SaitamaAd} from '@adv-mt/advertising-publift'
import CardAd, {FETCHPRIORITY} from '@adv-mt/mt-card-ad'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import DynamicRendering from '@adv-mt/mt-perf-dynamic-rendering'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import GridLayout from './gridLayout/index.js'
import {SaitamaAdMultiformatStyled} from './SaitamaAdMultiformatStyled/index.js'
import {useViewedAds} from './useViewedAds/index.js'

const EmptyContent = () => <></>

const DESKTOP_HEIGHT = {
  DEFAULT: 187,
  LARGE: 220
}
const MOBILE_HEIGHT = {DEFAULT: 446, LARGE: 479}

const getAdSpace = ({adSpaces = [], index}) => adSpaces.find(({position}) => index === position) || null

// An adSpace may declare `renderComponent` instead of `component` so it can adapt to the layout it is
// injected into (e.g. a carousel matching the number of visible cards to the grid columns). Layouts
// with no columns leave `gridColumns` undefined and the component falls back to its own default.
const renderAdSpace = ({adSpace, gridColumns}) =>
  adSpace?.renderComponent?.({gridColumns}) ?? adSpace?.component ?? null

const linkFactoryDefaultProp = ({children, ...rest}) => <a {...rest}>{children}</a>

const getLinkFactoryWithState =
  ({linkFactory: LinkFactoryRendered = linkFactoryDefaultProp(), ...params}) =>
  props =>
    <LinkFactoryRendered {...props} state={{...params}} />

const ListAds = ({
  ads = [],
  adSpaces = [],
  count,
  currentPage = 1,
  emptyCase = <EmptyContent />,
  extendedTrackingData = {},
  favoriteTrackingOrigin,
  hasDiscardButton,
  hasEntryOrMaxTermsFiltersApplied = false,
  hasMenu,
  isFavoritesList = false,
  isGridLayout = false,
  linkFactory,
  pageName,
  withAdViewedTracking = false,
  onAdsChanged = () => {},
  enableComparator,
  handleSelectComparar,
  comparatorSelectedAds
}) => {
  const [currentAds, setCurrentAds] = useState(ads)

  const {push} = useRouter()
  const {domain} = useContext(Context)
  const {track} = useAnalyticsTrack()

  const delayToNextFrame = useDelayToNextFrame()

  useEffect(() => {
    setCurrentAds(ads)
  }, [ads])

  const isLoading = typeof count !== 'number'

  const {generateAttribute} = useViewedAds({
    ads: currentAds,
    currentPage,
    extendedTrackingData,
    pageName
  })

  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const isTabletOrWider = useMediaQuery(useMediaQueryBreakpoints.md, {
    defaultMatches: !isMobile
  })

  const {
    ADS_LIST_POSITIONS,
    AD_FORMAT_MULTIFORMAT_DESKTOP,
    AD_FORMAT_NATIVE_DESKTOP,
    AD_FORMAT_NATIVE_MOBILE,
    AD_INLINE_BANNER_DESKTOP,
    AD_INLINE_BANNER_MOBILE,
    NO_ADVERTISING_PAGES,
    TextAds
  } = config.get('advertising')
  const {adsListSearchOfferTypes: enabledComparatorOfferTypes} = config.get('search')
  const {ENABLED_COMPARATOR_CATEGORY_IDS: enabledComparatorCategoryIds} = config.get('search')

  // Two full rows worth of cards, matching the grid column count per breakpoint (1/2/3/4).
  // Outside the grid layout the list is single-column so 2 is enough.
  const preRenderedAdsCount = isMobile ? 2 : 8

  const handleCardAdHide = ({id}) => {
    setCurrentAds(prevState => {
      const updatedAds = prevState.filter(ad => ad.id !== id)
      onAdsChanged(updatedAds) // Sync parent with updated ads
      return updatedAds
    })
  }

  if (isLoading) return <EmptyContent />
  if (count === 0) return emptyCase

  const reservedHeight = isMobile || isFavoritesList || isGridLayout ? MOBILE_HEIGHT : DESKTOP_HEIGHT

  const {PAGES_WITH_PRICE_DROP} = config.get('page')
  const showPriceDrop = PAGES_WITH_PRICE_DROP.includes(pageName)

  const cardAdProps = {
    cardClass: 'mt-ListAds-item',
    hasDiscardButton,
    hasMenu,
    isFavoritesList,
    showPriceDrop,
    vertical: isGridLayout || !isTabletOrWider || isFavoritesList,
    favoriteTrackingOrigin
  }

  const classNames = cx('mt-ListAds', {
    'mt-ListAds--rowItems': isFavoritesList
  })

  const textAdsId = ADS_LIST_POSITIONS[TextAds]?.id
  const hasAdvertising = !NO_ADVERTISING_PAGES.includes(pageName)
  const advertisementPositions = Object.keys(ADS_LIST_POSITIONS)
    .filter(pos => Boolean(ADS_LIST_POSITIONS[pos]))
    .filter(
      pos =>
        !ADS_LIST_POSITIONS[pos].format ||
        isMobile === !!ADS_LIST_POSITIONS[pos].isMobile ||
        !isMobile === !!ADS_LIST_POSITIONS[pos].isDesktop
    )

  const getAdvertisements = ({advertisements}) =>
    advertisements
      .map(ad => ADS_LIST_POSITIONS[ad])
      .map(({id, format}) => {
        if (!format) return null

        return (
          <SaitamaAdMultiformatStyled
            key={id}
            id={id}
            className={cx('mt-ListAds-item', {
              'mt-ListAds-item--native--mobile':
                format === AD_FORMAT_NATIVE_MOBILE || (format === AD_FORMAT_NATIVE_DESKTOP && isGridLayout),
              'mt-ListAds-item--native--desktop': format === AD_FORMAT_NATIVE_DESKTOP && !isGridLayout,
              'mt-ListAds-item--multiformat--desktop': format === AD_FORMAT_MULTIFORMAT_DESKTOP,
              'mt-ListAds-item--banner--mobile ': format === AD_INLINE_BANNER_MOBILE,
              'mt-ListAds-item--banner--desktop': format === AD_INLINE_BANNER_DESKTOP
            })}
            isAlwaysNative={[AD_FORMAT_NATIVE_MOBILE, AD_FORMAT_NATIVE_DESKTOP].includes(format)}
          />
        )
      })

  const listOfAds = currentAds.map((ad, index) => {
    const extendedAdTrackingData = {
      filters: extendedTrackingData.filters,
      provinces: extendedTrackingData.provinces,
      product: extendedTrackingData.products?.find(product => product.id === String(ad.id)),
      ...(hasEntryOrMaxTermsFiltersApplied && {financingData: ad.financingInfo})
    }

    // The card fires events (e.g. Ad Favorited) and needs the page it is in. Not part of the navigation state.
    const cardTrackingData = {
      ...extendedAdTrackingData,
      page_name: extendedTrackingData.page_name,
      page_type: extendedTrackingData.page_type
    }

    const handleClickAd = ({id, url}) => {
      push({
        pathname: url,
        state: {...extendedAdTrackingData, origin: pageName, from: pageName}
      })
      delayToNextFrame(() => {
        return isSubscription && isExternalTarget && trackOnAdClicked(id)
      })
    }

    const {financingInfo, hidePhone, isExternalTarget, isSubscription, partnerName} = ad
    const hasLastImageContactButton = !isTabletOrWider && !hidePhone
    const needsLargeHeight = Boolean(financingInfo)

    if (isFavoritesList) {
      const isEnabledCategory = enabledComparatorCategoryIds?.includes(ad?.category1Id)
      const isEnabledOfferType = enabledComparatorOfferTypes?.some(({id}) => id === ad.offerType?.id)

      return [
        <div {...(withAdViewedTracking && generateAttribute({position: index}))} data-ad-id={ad.id} key={ad.id}>
          <DynamicRendering key={ad.id} height={needsLargeHeight ? reservedHeight.LARGE : reservedHeight.DEFAULT}>
            <CardAd
              ad={ad}
              contactable
              extendedTrackingData={cardTrackingData}
              hasLastImageContactButton={hasLastImageContactButton}
              linkFactory={getLinkFactoryWithState({
                linkFactory,
                ...extendedAdTrackingData
              })}
              {...cardAdProps}
              pageName={pageName}
              onCardAdHide={handleCardAdHide}
              onClick={handleClickAd}
              enableComparator={enableComparator && isEnabledCategory && isEnabledOfferType}
              handleSelectComparar={handleSelectComparar}
              comparatorSelectedAds={comparatorSelectedAds}
              isGridLayout={isGridLayout}
              isFavoritesList
            />
          </DynamicRendering>
        </div>,
        adSpaces.length > 0 && renderAdSpace({adSpace: getAdSpace({adSpaces, index})})
      ]
    }
    const advertisements = hasAdvertising
      ? advertisementPositions.filter(pos => ADS_LIST_POSITIONS[pos].position === index + 1)
      : []

    const trackOnAdClicked = id => {
      track({
        event: 'Bipi Ad Clicked',
        data: {
          products: [
            {
              id,
              category: `${partnerName ? partnerName + '-' : ''}ads`,
              quantity: 0,
              price: 0
            }
          ]
        }
      })
    }

    const dynamicAdsThreshold = `${reservedHeight.LARGE}px 0px`

    const getLinkFactory = () => {
      return getLinkFactoryWithState({
        linkFactory,
        ...extendedAdTrackingData
      })
    }

    return [
      !isGridLayout && advertisements.length > 0 && getAdvertisements({advertisements}),
      !isGridLayout && adSpaces.length > 0 && renderAdSpace({adSpace: getAdSpace({adSpaces, index})}),
      index < preRenderedAdsCount ? (
        <div
          {...(withAdViewedTracking && generateAttribute({position: index}))}
          data-ad-id={ad.id}
          key={ad.id}
          className="mt-ListAds-item"
        >
          <CardAd
            {...cardAdProps}
            ad={ad}
            extendedTrackingData={cardTrackingData}
            fetchpriority={FETCHPRIORITY.HIGH}
            hasLastImageContactButton={hasLastImageContactButton}
            isExternalTarget={isExternalTarget}
            linkFactory={getLinkFactory()}
            onCardAdHide={handleCardAdHide}
            onClick={handleClickAd}
            pageName={pageName}
            enableComparator={enableComparator}
            handleSelectComparar={handleSelectComparar}
            comparatorSelectedAds={comparatorSelectedAds}
            isGridLayout={isGridLayout}
          />
        </div>
      ) : (
        <div
          {...(withAdViewedTracking && generateAttribute({position: index}))}
          data-ad-id={ad.id}
          key={ad.id}
          className="mt-ListAds-item"
        >
          <DynamicRendering
            height={needsLargeHeight ? reservedHeight.LARGE : reservedHeight.DEFAULT}
            placeholder={
              <div
                style={{
                  height: needsLargeHeight ? reservedHeight.LARGE : reservedHeight.DEFAULT
                }}
                data-ad-id={ad.id}
              />
            }
            rootMargin={dynamicAdsThreshold}
          >
            <CardAd
              {...cardAdProps}
              ad={ad}
              extendedTrackingData={cardTrackingData}
              fetchpriority={FETCHPRIORITY.AUTO}
              hasLastImageContactButton={hasLastImageContactButton}
              isExternalTarget={isExternalTarget}
              linkFactory={getLinkFactory()}
              onCardAdHide={handleCardAdHide}
              onClick={handleClickAd}
              pageName={pageName}
              enableComparator={enableComparator}
              handleSelectComparar={handleSelectComparar}
              comparatorSelectedAds={comparatorSelectedAds}
              isGridLayout={isGridLayout}
            />
          </DynamicRendering>
        </div>
      )
    ]
  })

  // A space at position === ads.length has no card to go before (e.g. the visited ad is the last one
  // of the page), so it trails the list. The grid still snaps it to its fixed row when it has columns.
  const trailingAdSpace = isFavoritesList ? null : getAdSpace({adSpaces, index: currentAds.length})

  return (
    <>
      {isGridLayout ? (
        <GridLayout
          listOfAds={listOfAds.flatMap((ad, index) => {
            // position === index + 2 so it does not place next to the tallAd
            // once experiment is approved change the position on the ADS_LIST_POSITIONS in the domain
            const inlineAdvertisements = hasAdvertising
              ? advertisementPositions.filter(pos => ADS_LIST_POSITIONS[pos].position === index + 2)
              : []
            // Kept unrendered: the grid resolves it in `getGridItem`, where the column count is known.
            const adSpace = adSpaces.length > 0 ? getAdSpace({adSpaces, index}) : null

            // The adSpace goes BEFORE its card, matching the flat list above: a space at position P
            // renders between cards P-1 and P. Single-column grids keep this order untouched, so the
            // block lands in the same place it did before the grid layout existed.
            const result = adSpace ? [{_adSpace: true, adSpace}, ad] : [ad]
            if (inlineAdvertisements.length > 0) {
              result.push({_inlineAd: true, advertisements: inlineAdvertisements})
            }
            if (trailingAdSpace && index === listOfAds.length - 1) {
              result.push({_adSpace: true, adSpace: trailingAdSpace})
            }
            return result
          })}
          getGridItem={(item, {columnsCount}) => {
            if (item._inlineAd) return getAdvertisements({advertisements: item.advertisements})
            if (item._adSpace) return renderAdSpace({adSpace: item.adSpace, gridColumns: columnsCount})
            return item
          }}
          pageName={pageName}
        />
      ) : (
        <div className={classNames}>
          {listOfAds}
          {renderAdSpace({adSpace: trailingAdSpace})}
        </div>
      )}
      {hasAdvertising && textAdsId && (
        <div className="mt-ListAds-item--textads">
          <SaitamaAd id={textAdsId} />
        </div>
      )}
    </>
  )
}

ListAds.displayName = 'ListAds'

ListAds.propTypes = {
  adSpaces: PropTypes.arrayOf(
    PropTypes.shape({
      position: PropTypes.number,
      component: PropTypes.node,
      renderComponent: PropTypes.func
    })
  ),
  ads: PropTypes.array,
  count: PropTypes.number,
  currentPage: PropTypes.number,
  emptyCase: PropTypes.node,
  extendedTrackingData: PropTypes.object,
  favoriteTrackingOrigin: PropTypes.string,
  hasDiscardButton: PropTypes.bool,
  hasMenu: PropTypes.bool,
  hasEntryOrMaxTermsFiltersApplied: PropTypes.bool,
  isFavoritesList: PropTypes.bool,
  isGridLayout: PropTypes.bool,
  linkFactory: PropTypes.func,
  linkFactoryState: PropTypes.object,
  pageName: PropTypes.string,
  withAdViewedTracking: PropTypes.bool,
  onAdsChanged: PropTypes.func,
  enableComparator: PropTypes.bool,
  handleSelectComparar: PropTypes.func,
  comparatorSelectedAds: PropTypes.array
}

export default ListAds
